// File: src/pages/AdminDashboard.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid
} from '@mui/material';

const AdminDashboard = () => {
  // Sample user data for demonstration purposes
  const [users, setUsers] = useState([
    { id: 1, name: 'John Doe', role: 'user', email: 'john@example.com' },
    { id: 2, name: 'Jane Smith', role: 'manager', email: 'jane@example.com' },
    { id: 3, name: 'Alice Johnson', role: 'user', email: 'alice@example.com' },
  ]);

  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');

  // Hard-coded concurrency stats (for demonstration)
  const concurrencyStats = {
    Year: 5000,
    Month: 1200,
    Week: 300,
    Today: 40,
    Yesterday: 35,
  };

  const handleAddUser = () => {
    if (!newUserName || !newUserEmail) return;
    const newUser = {
      id: Date.now(),
      name: newUserName,
      role: 'user',
      email: newUserEmail,
    };
    setUsers([...users, newUser]);
    setNewUserName('');
    setNewUserEmail('');
  };

  const handleRemoveUser = (id) => {
    setUsers(users.filter((user) => user.id !== id));
  };

  const handleUpgradeToManager = (id) => {
    setUsers(users.map(user => user.id === id ? { ...user, role: 'manager' } : user));
  };

  const handleAddBlogContent = () => {
    alert('Add blog content feature coming soon!');
  };

  return (
    <Box
      sx={{
        pt: '12%', // Increase top spacing by 12%
        px: { xs: 2, md: 3 },
        minHeight: '100vh',
        backgroundColor: '#f0f2f5', // light background
        color: '#2c3e50',         // dark text for contrast
      }}
    >
      {/* Header */}
      <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold', color: '#f39c12' }}>
        Admin Dashboard
      </Typography>

      {/* Concurrency Stats */}
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
        {Object.entries(concurrencyStats).map(([label, value]) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                {label}
              </Typography>
              <Typography variant="h6" sx={{ color: '#f39c12' }}>
                {value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

      {/* Blog Content Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#2c3e50' }}>
          Add Content to Community Blog
        </Typography>
        <Button
          variant="contained"
          onClick={handleAddBlogContent}
          sx={{
            backgroundColor: '#f39c12',
            color: '#2c3e50',
            '&:hover': { backgroundColor: '#e67e22' },
          }}
        >
          Add Blog Post
        </Button>
      </Box>

      <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />

      {/* Manage Users Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#2c3e50' }}>
          Manage Users
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <TextField
            label="Name"
            value={newUserName}
            onChange={(e) => setNewUserName(e.target.value)}
            sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}
            fullWidth
          />
          <TextField
            label="Email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ backgroundColor: '#ffffff', borderRadius: 1 }}
            fullWidth
          />
          <Button variant="contained" onClick={handleAddUser} sx={{ backgroundColor: '#f39c12', color: '#2c3e50' }}>
            Add User
          </Button>
        </Box>
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#2c3e50' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#2c3e50' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#2c3e50' }}>Email</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      sx={{ mr: 1, borderColor: '#f39c12', color: '#f39c12' }}
                      onClick={() => handleRemoveUser(user.id)}
                    >
                      Remove
                    </Button>
                    {user.role !== 'manager' && (
                      <Button
                        variant="contained"
                        onClick={() => handleUpgradeToManager(user.id)}
                        sx={{ backgroundColor: '#f39c12', color: '#2c3e50' }}
                      >
                        Upgrade to Manager
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Revenue Overview Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: '#2c3e50' }}>
          Revenue Overview
        </Typography>
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" sx={{ color: '#2c3e50' }}>
            Revenue charts and detailed reports coming soon...
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
